import {
  StopCircleIcon,
  TableCellsIcon,
  PhotoIcon,
  DocumentChartBarIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import { CubeIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { IconButton, Tooltip, Spinner, Badge } from "@material-tailwind/react";
import { t } from "i18next";
import { ErrorIcon } from "../icons/ErrorIcon";
import { StatusChangeMenu } from "./StatusChangeMenu";
import { ExportCSVButton } from "./ExportCSVButton";
import { ExportMediaButton } from "./ExportMediaButton";
import { isOdooConnected } from "../../services/odoo/odoo";
import {
  canDestockWorkorder,
  canSendWorkorderToOdoo,
} from "../../services/odoo/workorderOdoo";
import { isRomaniaRealm } from "../../services/utils/utils";

// Composant interne pour gérer l'affichage du badge de statut (Succès/Erreur/Attention)
const ActionStatusBadge = ({
  success,
  error,
  warning,
  children
}: {
  success: boolean;
  error: boolean;
  warning?: boolean;
  children: React.ReactNode
}) => {
  if (!success && !error && !warning) return children;

  let badgeContent = <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />;
  let bgColor = "bg-[#209933]"; // Green

  if (error) {
    badgeContent = <ErrorIcon className="h-4 w-4" />;
    bgColor = "bg-[#F50A0A]"; // Red
  } else if (warning) {
    badgeContent = <ExclamationTriangleIcon className="h-3.5 w-3.5 text-white" />;
    bgColor = "bg-[#F59E0B]"; // Orange
  }

  const badgeClass = `!min-w-[24px] !min-h-[24px] !w-[24px] !h-[24px] p-0 flex items-center justify-center ${bgColor}`;

  return (
    <Badge
      content={badgeContent}
      className={badgeClass}
      overlap="circular"
    >
      {children}
    </Badge>
  );
};

interface WorkorderHeaderActionsProps {
  workorder: EasyTech.Workorder;
  onStatusChange: () => Promise<void>;
  onExportPDF: () => void;
  onSendToOdoo: () => void;
  onDestock: () => void;
  sendToOdooLoading: boolean;
  sendToOdooInProgress?: boolean;
  destockLoading: boolean;
  isOdooDestockError?: boolean;
  photos: any[];
  signatures: any[];
  getPhotoDownloadUrl: (path: string) => string;
  getSignatureDownloadUrl: (path: string) => string;
}

export const WorkorderHeaderActions = ({
  workorder,
  onStatusChange,
  onExportPDF,
  onSendToOdoo,
  onDestock,
  sendToOdooLoading,
  sendToOdooInProgress = false,
  destockLoading,
  isOdooDestockError = false,
  photos,
  signatures,
  getPhotoDownloadUrl,
  getSignatureDownloadUrl,
}: WorkorderHeaderActionsProps) => {

  console.log("WorkorderHeaderActions debug:", {
    ref: workorder.ref,
    status: workorder.status,
    hasInvoice: !!workorder.invoice,
    stockStatus: workorder.invoice?.stock?.status,
    hosesCount: workorder.hoses?.length,
    hoses: workorder.hoses
  });

  const actions = [
    { icon: <StopCircleIcon className="icon" />, type: "status-change" },
    { icon: <TableCellsIcon className="icon" />, type: "export" },
    { icon: <PhotoIcon className="icon" />, type: "export-media" },
    {
      icon: <DocumentChartBarIcon className="icon" />,
      type: "export-pdf",
      onClick: onExportPDF,
    },
    ...(!isRomaniaRealm() ? [{
      icon: <PaperAirplaneIcon className="icon -rotate-90" style={{ color: "#E5980D" }} strokeWidth={2} />,
      type: "send-to-odoo",
      onClick: onSendToOdoo,
    }] : []),
    ...(isRomaniaRealm() ? [{
      icon: <CubeIcon className="icon" style={{ color: "#D06363" }} strokeWidth={2} />,
      type: "destock",
      onClick: onDestock,
    }] : [])
  ];

  return (
    <div className="icon-buttons">
      {actions.map((action, index) => {
        if (action.type === "status-change") {
          return (
            <StatusChangeMenu
              key={index}
              id={workorder?.technician?.id || ""}
              workorderId={workorder?.id || ""}
              workorderRef={workorder?.ref || ""}
              currentStatus={workorder?.status || ""}
              onStatusChange={onStatusChange}
              workorder={workorder}
            >
              <IconButton className="icon-button bg-white" aria-label="Change status">
                {action.icon}
              </IconButton>
            </StatusChangeMenu>
          );
        } else if (action.type === "export") {
          return (
            <ExportCSVButton
              key={index}
              workorder={workorder}
              className="icon-button-container"
            >
              <Tooltip
                content={t("workorder:action:exportCSV")}
              >
                <IconButton className="icon-button bg-white" aria-label="Export CSV">
                  {action.icon}
                </IconButton>
              </Tooltip>
            </ExportCSVButton>
          );
        } else if (action.type === "export-media") {
          return (
            <ExportMediaButton
              key={index}
              workorder={workorder}
              className="icon-button-container"
              photos={photos}
              signatures={signatures}
              getPhotoDownloadUrl={getPhotoDownloadUrl}
              getSignatureDownloadUrl={getSignatureDownloadUrl}
            >
              <Tooltip
                content={t("workorder:action:exportMedia")}
              >
                <IconButton className="icon-button bg-white" aria-label="Export Media">
                  {action.icon}
                </IconButton>
              </Tooltip>
            </ExportMediaButton>
          );
        } else if (action.type === "export-pdf") {
          return (
            <Tooltip
              key={index}
              content={t("workorder:report:downloadReport")}
            >
              <IconButton
                className="icon-button bg-white"
                onClick={action.onClick}
                disabled={!["VALIDATED", "CLOSED", "INVOICED", "PAID"].includes(workorder.status)}
                title={t("workorder:report:downloadReport")}
                aria-label="Export PDF"
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          );
        } else if (action.type === "send-to-odoo" && isOdooConnected()) {
          const hasInvoice = !!workorder.invoice;
          // Un envoi deja en vol, qu'il vienne de cet onglet ou d'ailleurs, se lit pareil : bouton
          // bloque et spinner, plutot que de laisser le core repondre IN_PROGRESS en erreur.
          const isSendingToOdoo = sendToOdooLoading || sendToOdooInProgress;
          const isSendToOdooDisabled = !canSendWorkorderToOdoo({
            workorder,
            loading: isSendingToOdoo,
          });
          const buttercup500 = "#F59E0B"; // Buttercup 500
          const gray300 = "#D1D5DB"; // Gray 300
          const gray50 = "#F9FAFB"; // Gray 50

          // Calcul des états pour le badge

          return (
            <Tooltip
              key={index}
              content={
                sendToOdooInProgress
                  ? t("workorder:odoo:inProgress")
                  : t("workorder:action:sendToOdoo")
              }
            >
              <ActionStatusBadge success={hasInvoice} error={false}>
                <IconButton
                  className="icon-button"
                  color="white"
                  style={{
                    backgroundColor: isSendToOdooDisabled ? gray50 : "#FFFFFF",
                    border: `2px solid ${isSendToOdooDisabled ? gray300 : buttercup500}`,
                    boxShadow: isSendToOdooDisabled ? "none" : "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
                  }}
                  onClick={action.onClick}
                  disabled={isSendToOdooDisabled}
                  aria-label="Send to Odoo"
                >
                  {isSendingToOdoo ? (
                    <Spinner className="h-5 w-5" color="amber" />
                  ) : (
                    <PaperAirplaneIcon
                      className="icon -rotate-90"
                      style={{ color: isSendToOdooDisabled ? gray300 : buttercup500 }}
                      strokeWidth={2}
                    />
                  )}
                </IconButton>
              </ActionStatusBadge>
            </Tooltip>
          );
        } else if (action.type === "destock" && isOdooConnected()) {
          const stock = workorder.invoice?.stock;
          const isStockSuccess = stock && stock.status === 'SUCCESS';
          const isDestockDisabled = !canDestockWorkorder({
            workorder,
            loading: destockLoading,
          });
          const sanguineBrown500 = "#DC2626"; // Sanguine brown 500
          const gray300 = "#D1D5DB"; // Gray 300
          const gray50 = "#F9FAFB"; // Gray 50

          // Calcul des états pour le badge stock

          return (
            <Tooltip
              key={index}
              content={t("workorder:action:destocked")}
            >
              <ActionStatusBadge success={!!isStockSuccess} error={false} warning={false}>
                <IconButton
                  className="icon-button"
                  color="white"
                  style={{
                    backgroundColor: isDestockDisabled ? gray50 : "#FFFFFF",
                    border: `2px solid ${isDestockDisabled ? gray300 : sanguineBrown500}`,
                    boxShadow: isDestockDisabled ? "none" : "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
                  }}
                  onClick={action.onClick}
                  disabled={isDestockDisabled}
                  aria-label="Destock"
                >
                  {destockLoading || workorder.invoice?.stock?.status === 'IN_PROGRESS' ? (
                    <Spinner className="h-5 w-5" color="red" />
                  ) : (
                    <CubeIcon
                      className="icon"
                      style={{ color: isDestockDisabled ? gray300 : sanguineBrown500 }}
                      strokeWidth={2}
                    />
                  )}
                </IconButton>
              </ActionStatusBadge>
            </Tooltip>
          );
        } else return null;
      })}
    </div>
  );
};
